<template>
  <el-row class="editPanel">
      <h3 :class="{closed: !panelOpen}">Cash Return on Investment (CRI)</h3><span class="toggle" @click="panelOpen = panelOpen !== true" :class="{closed: !panelOpen}"></span>
      <transition name="panel">
        <div class="pea-panel" v-if="panelOpen">
          <el-row :gutter="20">
            <el-col :span="24">
              <el-row :gutter="20">
                <el-col class="card-header" :sm="24" :md="24" :lg="24">
                  <strong>
                    Note: For higher value PEAs you need to provide evidence of the financial benefits from this planned expenditure by completing a CRI document.
                  </strong>
                  <br />
                  Please download the template and update it with the values specific to your PEA. Once the document is complete you need to attach it to the PEA using 'Select & Upload CRI' below.
                  <br/>
                  You can repeat this process to provide an updated calculations.
                </el-col>
              </el-row>
              <el-row :gutter="20">
                <el-col :span="6">
                  <strong>Teleflex CRI Template:</strong>
                </el-col>
                <el-col :span="3">
                  <a :href="`${publicPath}documents/CRItemplate.xls`">Download the CRI Template</a>
                </el-col>
                <el-col :lg="1">
                  <tool-tip-popup text="Note: To provide evidence of the CRI you should complete a document based on the CRI template. To download the template right click on the link and select ''Save Link As'' to save the file locally. Start Excel and open the saved file, then fill in the values highlit in blue.  You should attach this document as part of the PEA and fill in the CRI figures in the PEA."></tool-tip-popup>
                </el-col>
              </el-row>
              <el-form :model="CRIData" ref="CRIForm">
                <el-row :gutter="20">
                  <el-col :span="6">
                    <strong>Metrics:</strong>
                  </el-col>
                  <el-col :span="18">
                    <el-row :gutter="20">
                      <el-col :span="8">
                        &nbsp;
                      </el-col>
                      <el-col :span="8">
                        <strong>Year 5</strong>
                      </el-col>
                      <el-col :span="8">
                        <strong>Year 10</strong>
                      </el-col>
                    </el-row>
                    <el-row :gutter="20">
                      <el-col :span="8">
                        <strong>Annual CRI</strong>
                      </el-col>
                      <el-col :span="8">
                        <el-input :disabled="disabled" type="number" v-model="CRIData.annualCRIYearFive"></el-input> %
                      </el-col>
                      <el-col :span="8">
                        <el-input :disabled="disabled" type="number" v-model="CRIData.annualCRIYearTen"></el-input> %
                      </el-col>
                    </el-row>
                    <el-row :gutter="20">
                      <el-col :span="8">
                        <strong>Cummulative CRI</strong>
                      </el-col>
                      <el-col :span="8">
                        <el-input :disabled="disabled" type="number" v-model="CRIData.cummulativeCRIYearFive"></el-input> %
                      </el-col>
                      <el-col :span="8">
                        <el-input :disabled="disabled" type="number" v-model="CRIData.cummulativeCRIYearTen"></el-input> %
                      </el-col>
                    </el-row>
                  </el-col>
                </el-row>
              </el-form>
              <el-row :gutter="20">
                <el-col :span="6">
                  <strong>Calculation Document:</strong>
                </el-col>
                <el-col :lg="1">
                  <tool-tip-popup text="Note: You can only attach one CRI document. If a CRI document is already uploaded and you want to upload an updated CRI document, delete the existing CRI document and then upload the new one."></tool-tip-popup>
                </el-col>
                <el-col :span="13">
                  <div v-if="hasFile">
                    Delete the existing CRI document in order to upload a new one.
                    <br />
                    <p v-if="usingIE" class="IEWarn">
                      <strong> Please SAVE file when prompted in IE do not open directly.
                      </strong>
                    </p>
                  </div>
                  <div v-if="!hasFile">
                    {{this.MaxFileSizeMessage}}
                    <br />
                  </div>
                  <div>
                    <el-upload :disabled="disableButton" :file-list="CRIDocumentFiles" ref="FileUploadCRI" v-bind:action="this.apiPath + '/pea/' + this.peaId + '/addDocument/CRI'"
                            :headers="checkReturnAuthHead" :on-success="uploadCRISuccess" :on-error="uploadCRIFailure" :limit="1" :before-upload="startUpload"
                            :on-preview="downloadDocument" :on-remove="removeFile" :on-exceed="fileCountExceeded">
                            <el-button :disabled="isLoading || disableButton" v-if="!disableButton && !hasFile" slot="trigger" type="primary">Select & Upload CRI</el-button>
                    </el-upload>
                  </div>
                  <br v-if="isLoading"/>
                  <span v-if="isLoading">{{this.loadingMessage}}</span>
                  <span class="el-icon-loading" v-if="isLoading"></span>
                  <span v-if="!isLoading">&nbsp;</span>
                </el-col>
              </el-row>
            </el-col>
          </el-row>
        </div>
      </transition>
  </el-row>
</template>

<script>

import helperMethods from '@/components/utility/helperMethods.js'
import { mapActions } from 'vuex'

export default {
  name: 'pea-cri',
  props: {
    peaId: {
      default: 0
    },
    disabled: {
      default: false
    },
    disableButton: {
      default: false
    }
  },
  data () {
    return {
      CRIData: {
        annualCRIYearFive: '',
        annualCRIYearTen: '',
        cummulativeCRIYearFive: '',
        cummulativeCRIYearTen: ''
      },
      CRIDocumentFiles: [],
      isLoading: false,
      loadingType: '',
      apiPath: process.env.VUE_APP_API_SERVER,
      panelOpen: true,
      MaxFileSizeMessage: '',
      hasFile: false,
      loadingMessage: '',
      usingIE: false,
      publicPath: process.env.BASE_URL
    }
  },
  computed: {
    checkReturnAuthHead () {
      if (this.$store.state.userInfo.accessToken) {
        return {
          'Authorization': 'Bearer ' + this.$store.state.userInfo.accessToken
        }
      } else {
        // eslint-disable-next-line
        this.getOrSetAuth().then(() => {
          return {
            'Authorization': 'Bearer ' + this.$store.state.userInfo.accessToken
          }
        })
      }
    }
  },
  methods: {
    ...mapActions(['getAccessToken', 'getDataToken', 'getSingleFullAccountData', 'getUserDetails']),
    getOrSetAuth () {
      return new Promise((resolve) => {
        const accounts = this.$msalInstance.getAllAccounts()
        this.$msalInstance.setActiveAccount(accounts[0])
        this.$store.state.userInfo.account = accounts[0]
        this.getDataToken(this.$msalInstance).then(() => {
          this.getSingleFullAccountData(this.$store.state.userInfo.account.username)
            .then(() => {
              this.getUserDetails(this.$store.state.userInfo.fullAccount.onPremisesSamAccountName)
              resolve()
            })
        })
      })
    },
    startup () {
      this.fetchCRIData()
      this.refreshFileList()
      this.fetchMaxFileSize()
      this.checkIE()
    },
    checkIE () {
      var browser = helperMethods.getBrowser()
      if (browser.name === 'MSIE' || browser.name === 'IE') {
        this.usingIE = true
      }
    },
    fetchCRIData () {
      var call = '/getCRIInfo/' + this.peaId
      this.$axios.get(call).then(response => {
        this.CRIData = response.data
      }).catch(response => {
        helperMethods.showNotification(this.$parent, 'Error retrieving CRI Info', response.body, 'error', 30000)
      })
    },
    fetchMaxFileSize () {
      var call = '/ApiInfo/MaxFileSize'
      this.$axios.get(call).then(response => {
        this.maxFileSize = response.data
        this.MaxFileSizeMessage = 'Maximum size of file is ' + response.data
      }).catch(response => {
        helperMethods.showNotification(this.$parent, 'Error retrieving MaxFileSize', response.body, 'error', 30000)
      })
    },
    updateCRI () {
      var call = '/updateCRIforPEA/' + this.peaId
      return new Promise((resolve, reject) => {
        this.$axios.post(call, this.CRIData).then(response => resolve(response))
          .catch(response => {
            helperMethods.showNotification(this.$parent, 'Error updating CRI Information', response.body, 'error', 30000)
            reject(response)
          })
      })
    },
    fileCountExceeded (files, fileList) {
      this.hasFile = true
      this.$notify({
        title: 'Failure',
        message: 'Cannot upload more than one CRI file.  If a CRI document is already uploaded and you want to upload an updated CRI document, delete the existing CRI document and then upload the new one.',
        type: 'warning'
      })
    },
    refreshFileList () {
      var call = '/pea/' + this.peaId + '/getDocuments/CRI'
      this.$axios.get(call).then(response => {
        this.CRIDocumentFiles = response.data
        if (this.CRIDocumentFiles.length > 0) {
          this.hasFile = true
        }
        this.$emit('fetchComplete')
      }).catch(response => {
        helperMethods.showNotification(this.$parent, 'Error retrieving CRI documents', response.body, 'error', 30000)
        this.$emit('fetchComplete')
      })
    },
    downloadDocument (file) {
      this.isLoading = true
      this.loadingMessage = 'Downloading CRI Document'
      this.$axios.get('/documents/getDocumentContents/' + file.id, { responseType: 'blob' }).then(response => {
        if (navigator.msSaveBlob) { // IE 10+
          let blob = new Blob([response.data], { type: response.headers.get('content-type') })
          navigator.msSaveBlob(blob, file.name)
          this.isLoading = false
        } else {
          let blob2 = new Blob([response.data], { type: response.headers.get('content-type') })
          let link = document.createElement('a')
          link.href = window.URL.createObjectURL(blob2)
          link.setAttribute('download', file.name)
          document.body.appendChild(link)
          link.click()
          this.isLoading = false
        }
      })
    },
    uploadCRISuccess (response, file, fileList) {
      this.isLoading = false
      this.hasFile = true
      helperMethods.showNotification(this.$parent, 'Success', 'CRI file uploaded.', 'success', 30000)
      this.refreshFileList()
    },
    uploadCRIFailure (err, file, fileList) {
      this.isLoading = false
      helperMethods.showNotification(this.$parent, 'CRI File Error', err.toString(), 'error', 30000)
      this.refreshFileList()
    },
    startUpload (file) {
      var t1 = this.maxFileSize.split(' ')
      var intSize = parseInt(t1[0])
      var isLt4M = parseInt((file.size / 1024) / 1024) < intSize
      if (!isLt4M) {
        this.$notify.error({
          title: 'File Too Large',
          message: 'File cannot exceed ' + this.maxFileSize + '.'
        })
        this.refreshFileList()
        return false
      } else {
        this.loadingType = ''
        this.isLoading = true
      }
    },
    removeFile (file, fileList) {
      if (!this.disableButton) {
        if (file.id !== undefined) {
          var documentId = file.id
          this.isLoading = true
          var call = '/documents/deleteDocument/' + documentId
          this.$axios.delete(call).then(data => {
            this.isLoading = false
            this.hasFile = false
            helperMethods.showNotification(this.$parent, 'Success', 'CRI file deleted.', 'success', 30000)
            this.refreshFileList()
          }).catch(response => {
            helperMethods.showNotification(this.$parent, 'Error deleting CRI Doc', response.body, 'error', 30000)
          })
        }
      }
    }
  },
  mounted () {
    this.startup()
  },
  watch: {
    // watch for route change - going from pea/4 to pea/0
    '$route': 'startUp'
  }
}
</script>

<style scoped>
.IEWarn {
  color: rgb(165,0,33);
}
a {
  color: #00a9ce;
}

.pea-panel .el-row {
  text-align: left;
  padding: 5px 0;
}

.el-input {
  width: 85%;
}

.rowWithInput strong {
  height: 36px;
  display: table-cell;
  vertical-align: middle;
}

@media (max-width: 1199px){
  .rowWithInput .el-col {
    margin-bottom: 10px;
  }
}
</style>
